import { BPDFormElement } from '@atoms/form-elements';
import { extendedArea } from '@bpd-library/utilities';
import { areaSubscriptionStore } from '@features/area-subscription/store';
import {
    AreaProjectNewsletterProps,
    ProjectPhaseNewsletterProps,
    PropertyTypeNewsletterProps,
} from '@store/subscription/services/api-calls';
import {
    getSubscriptionState,
    subscribeToAreaProject,
    subscribeToAreaProjectAndProjectPhases,
    subscribeToPropertyTypes,
} from '@store/subscription/store';
import { SubscriptionStoreAddress, SubscriptionStoreUser } from '@store/subscription/store.types';

type AreaSubscriptionFormData = {
    userInfo: SubscriptionStoreUser;
    addressInfo?: SubscriptionStoreAddress;
};

const extractFormData = (
    form: BPDFormElement | undefined,
    options: { areaOnly: boolean },
): AreaSubscriptionFormData => {
    const formData = {} as AreaSubscriptionFormData;
    const { formRequired } = areaSubscriptionStore.getState();

    if (formRequired) {
        if (!form) return formData;
        const {
            email,
            gender,
            country,
            city,
            street,
            zipcode,
            ['first-name']: firstName,
            ['last-name-prefix']: lastNamePrefix,
            ['last-name']: lastName,
            ['house-number']: houseNumber,
            ['house-number-addition']: houseNumberAddition,
            ['phone-number']: phoneNumber,
            website,
        } = form.serializeForm();

        formData.userInfo = { firstName, lastName, lastNamePrefix, email, gender, website };

        if (!options?.areaOnly) {
            formData.addressInfo = {
                zipcode,
                houseNumber,
                houseNumberAddition,
                street,
                city,
                phoneNumber,
                country,
            };
        }
    } else {
        const { user, address } = getSubscriptionState();
        formData.userInfo = user!;
        formData.addressInfo = address;
    }

    return formData;
};

const submitAreaForm = async (
    formData: AreaSubscriptionFormData,
): Promise<AreaProjectNewsletterProps> => {
    const {districtId, areaId} = window.__ENVIRONMENT_DETAILS__;
    const { areaProject } = await extendedArea.getData(areaId);

    const areaOnlyData: AreaProjectNewsletterProps = {
        areaInfo: { areaProjectId: areaProject.id, areaProjectName: areaProject.name!, ...(districtId && {districtId})},
        userInfo: formData.userInfo,
    };

    await subscribeToAreaProject(areaOnlyData);
    return areaOnlyData;
};

const submitAreaAndProjectPhasesForm = async (formData: AreaSubscriptionFormData) => {
    const { selectedProjectPhases, areaSelected } = areaSubscriptionStore.getState();
    const { projectPhases, areaProject } = await extendedArea.getData(
        window.__ENVIRONMENT_DETAILS__.areaId,
    );

    const selectedProjects = projectPhases.filter(({ id }) => selectedProjectPhases.includes(id));

    const projectForms = selectedProjects.map<ProjectPhaseNewsletterProps>((project) => ({
        areaInfo: {
            areaProjectId: areaProject.id,
            areaProjectName: areaProject.name || '',
            districtId: project?.districtId || '',
            projectPhaseId: project.id,
        },
        userInfo: formData.userInfo,
        address: formData.addressInfo,
    }));

    let areaProjectNewsletterProps: AreaProjectNewsletterProps | undefined;

    if (areaSelected) {
        areaProjectNewsletterProps = {
            areaInfo: {
                areaProjectId: areaProject.id,
                areaProjectName: areaProject.name || '',
                districtId: window.__ENVIRONMENT_DETAILS__?.districtId || '',
            },
            userInfo: formData.userInfo,
        };
    }

    await subscribeToAreaProjectAndProjectPhases(projectForms, areaProjectNewsletterProps);

    return projectForms;
};

const submitPropertyTypesForm = async (formData: AreaSubscriptionFormData) => {
    const { selectedPropertyTypes } = areaSubscriptionStore.getState();
    const { areaProject, propertyTypes } = await extendedArea.getData(
        window.__ENVIRONMENT_DETAILS__.areaId,
    );

    const selectedProjects = propertyTypes.filter(({ id }) => selectedPropertyTypes.includes(id));

    const propertyForms = selectedProjects.map<PropertyTypeNewsletterProps>((property) => ({
        areaInfo: {
            areaProjectId: areaProject.id,
            areaProjectName: areaProject.name || '',
            districtId: property?.districtId || '',
            projectPhaseId: property.projectPhaseId || '',
            propertyTypeId: property.id,
        },
        userInfo: formData.userInfo,
        address: formData.addressInfo!,
    }));

    await subscribeToPropertyTypes(propertyForms);

    return propertyForms;
};

const submitSelection = async (options: { step: number; formData: AreaSubscriptionFormData }) => {
    const { step, formData } = options;
    const { areaOnly } = areaSubscriptionStore.getState();

    switch (step) {
        case 0:
        case 1: {
            if (areaOnly) {
                await submitAreaForm(formData);
            } else {
                await submitAreaAndProjectPhasesForm(formData);
            }
            break;
        }
        case 2: {
            await submitPropertyTypesForm(formData);
            break;
        }
    }
};

export {
    extractFormData,
    submitAreaForm,
    submitAreaAndProjectPhasesForm,
    submitPropertyTypesForm,
    submitSelection,
};
